/**
 * Markdown, on top of fields that are full of plain text.
 *
 * Every descriptive field in the tracker — a decision's "why it matters", a roadmap
 * item's notes, a comment — was written as plain text into a field that rendered it as
 * plain text, and there are hundreds of those rows. Turning the renderer into a markdown
 * renderer is only safe if plain text comes out the other side looking exactly as it did,
 * so the pipeline is configured for that first and for markdown second:
 *
 * - `remark-breaks`, because a single newline in plain text is a line break to the person
 *   who typed it. CommonMark would fold those lines into one paragraph.
 * - `remarkHtmlAsText` below, because plain text contains angle brackets — `<selector>`,
 *   `<your domain>` — and a markdown parser reads those as HTML. Without this they would
 *   silently disappear from notes that already exist.
 * - No `rehype-raw`. Comments are written by client users and read by everyone else in
 *   the project, so nothing an author types may become live markup in another person's
 *   session. Raw HTML is shown as the characters that were typed, which is both safe and
 *   what a plain-text author meant.
 */

/** The parts of an mdast node this module touches. Avoids a dependency on `@types/mdast`. */
type MdNode = { type: string; value?: string; children?: MdNode[] }

/**
 * Where an `html` node is inline rather than a block of its own. A block becomes a
 * paragraph so it still renders inside the flow; inline text is spliced into the
 * surrounding sentence.
 */
const INLINE_PARENTS = new Set([
  'paragraph', 'heading', 'emphasis', 'strong', 'delete', 'link', 'linkReference', 'tableCell',
])

/** Renders anything the parser took for HTML as the literal characters instead. */
export function remarkHtmlAsText() {
  return (tree: MdNode) => {
    const walk = (node: MdNode) => {
      if (!node.children) return
      for (const child of node.children) {
        if (child.type === 'html') {
          const value = child.value ?? ''
          if (INLINE_PARENTS.has(node.type)) {
            child.type = 'text'
          } else {
            child.type = 'paragraph'
            child.children = [{ type: 'text', value }]
            delete child.value
          }
        } else {
          walk(child)
        }
      }
    }
    walk(tree)
  }
}

/**
 * A one-line, marker-free version of a note, for the card previews and `title` tooltips
 * that clamp to a line or two. Rendering markdown inside a two-line clamp would break the
 * card layout, and showing the raw `**` and `|` is worse than showing neither.
 *
 * Deliberately approximate: it is a preview, and the real thing is one click away.
 */
export function plainPreview(text: string): string {
  return (text ?? '')
    .replace(/```[\s\S]*?```/g, ' ')          // fenced code blocks
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')       // heading markers
    .replace(/^\s{0,3}>\s?/gm, '')            // block quotes
    .replace(/^\s{0,3}([-*+]|\d+[.)])\s+/gm, '') // list markers
    .replace(/^\s*\[[ xX]\]\s*/gm, '')        // task list boxes, once the marker has gone
    .replace(/^\s{0,3}([-*_])\s*(\1\s*){2,}$/gm, ' ') // horizontal rules
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links and images, keeping the label
    .replace(/`+/g, '')                        // code ticks
    .replace(/[*_~]{1,3}(?=\S)([\s\S]*?\S)[*_~]{1,3}/g, '$1') // emphasis around real text
    .replace(/\|/g, ' ')                       // table pipes
    .replace(/\s+/g, ' ')
    .trim()
}
